/**
 * WinnerModal - 中奖弹窗组件
 *
 * 基于截图设计实现的中奖结果展示弹窗。
 * 包含标题、奖品信息、优惠码和确认按钮。
 *
 * @module WinnerModal
 * @date 2025-12-24
 */

'use client'

import React from 'react'
import { cn } from '../../helpers/index.js'
import type { WinnerModalProps } from './types.js'
import { Picture, Heading, Button } from '../../components/index.js'
import { BaseModal } from './BaseModal.js'

// ============================================================================
// WinnerModal 组件
// ============================================================================

/**
 * WinnerModal 中奖弹窗组件
 *
 * 根据设计稿实现的中奖结果展示弹窗,包含:
 * - 半透明遮罩层
 * - 白色圆角卡片容器
 * - 标题 "You Have Won"
 * - 奖品名称
 * - 优惠券图片（20% Off）
 * - 优惠码和过期时间
 * - 确认按钮
 * - 右上角关闭按钮
 */
export const WinnerModal = ({
  isOpen,
  prize,
  title = 'You Have Won',
  prizeTitle,
  prizeImage,
  prizeDescription,
  learnMoreUrl,
  learnMoreText = 'Learn More',
  onClose,
  confirmText = 'Okay, Got It',
  onConfirm,
  confirmUrl,
  className,
  couponCode,
  couponDiscount,
  couponUnit = 'Off',
  expiresAt,
  codeText = 'CODE:',
  copyText = 'COPY',
  copiedText = 'COPIED',
  onCopyCode,
  theme = 'light',
}: WinnerModalProps & { theme?: 'light' | 'dark' }) => {
  // 复制状态
  const [copied, setCopied] = React.useState(false)
  const timerRef = React.useRef<NodeJS.Timeout | null>(null)

  React.useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  // 复制操作
  const handleCopy = (code: string) => {
    if (timerRef.current) clearTimeout(timerRef.current)

    navigator.clipboard.writeText(code)
    if (onCopyCode) onCopyCode?.(code)
    setCopied(true)
    timerRef.current = setTimeout(() => {
      setCopied(false)
    }, 3000)
  }

  // 不显示弹窗或无奖品数据时返回 null
  if (!isOpen || !prize) {
    return null
  }

  // 确认按钮点击处理
  const handleConfirm = () => {
    if (confirmUrl) {
      window.open(confirmUrl, '_blank', 'noopener,noreferrer')
    }
    onConfirm?.()
    onClose()
  }

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      className={className}
      maxWidth="max-w-[358px] laptop:max-w-[480px]"
      ariaLabelledBy="winner-modal-title"
      theme={theme}
    >
      <div className="laptop:px-8 laptop:pt-4 flex flex-col items-center px-6 pt-2">
        {/* =================================================================
           弹窗标题
          ================================================================= */}
        <div className="w-full">
          <Heading size={2} className={cn('laptop:mb-2 mb-1 text-center tracking-[-0.32px]')}>
            {title}
          </Heading>

          <p className="laptop:text-[18px] desktop:text-[16px] text-center text-[14px] font-[700] text-[#1D1D1F]">
            {prizeTitle || prize.name}
          </p>

          {/* =================================================================
           Learn More 按钮
          ================================================================= */}
          {learnMoreUrl && (
            <div className="laptop:mt-4 mt-3 text-center">
              <a
                href={learnMoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  'laptop:text-[14px] text-center text-[12px]',
                  'text-blue-600 hover:text-blue-700',
                  'font-medium underline',
                  'transition-colors'
                )}
              >
                {learnMoreText}
              </a>
            </div>
          )}
        </div>

        {/* =================================================================
           奖品图片
          ================================================================= */}

        <div
          className={cn(
            'mx-auto flex items-center justify-center',
            'laptop:my-6 laptop:size-[200px] my-4 size-[160px]'
          )}
        >
          {(prizeImage || prize.image) && (
            <Picture
              source={prizeImage || prize?.image?.url}
              alt={prize?.image?.alt || prizeTitle || prize?.name}
              className={cn('h-auto w-full')}
            />
          )}
        </div>

        {/* =================================================================
           折扣和优惠码复制
          ================================================================= */}
        {(couponDiscount || couponCode) && (
          <div className="laptop:mb-4 mb-2 flex w-full flex-col items-center">
            {couponDiscount && (
              <span className="text-brand-0 laptop:mb-2 laptop:text-[18px] mb-1 text-[16px] font-bold">
                {couponDiscount} {couponUnit}
              </span>
            )}
            {couponCode && (
              <div className="text-brand-0 laptop:text-[16px] flex items-center gap-2 text-center text-[14px] font-bold">
                <span className="max-w-[300px] truncate">
                  {codeText}
                  {couponCode}
                </span>
                <button onClick={() => handleCopy(couponCode!)} className="underline" style={{ minWidth: 60 }}>
                  {copied ? copiedText : copyText}
                </button>
              </div>
            )}
            {expiresAt && <span className="laptop:mt-2 mt-1 text-[12px] text-[#767880]">有效期至：{expiresAt}</span>}
          </div>
        )}

        {/* =================================================================
           奖品详情描述
          ================================================================= */}
        {prizeDescription && (
          <p
            className={cn('laptop:text-[16px] laptop:mb-2 mb-1 px-4 text-center text-[14px] font-[700] text-[#767880]')}
          >
            {prizeDescription}
          </p>
        )}
      </div>

      {/* =================================================================
           确认按钮
          ================================================================= */}
      <div className="laptop:px-8 laptop:pb-8 px-6 pb-6">
        <Button onClick={handleConfirm} className={cn('w-full')}>
          {confirmText}
        </Button>
      </div>
    </BaseModal>
  )
}

// ============================================================================
// 默认导出
// ============================================================================

export default WinnerModal
